import { useLocation } from "react-router-dom";
import "./DietPlan.css";

const DietPlan = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const category = searchParams.get("category");
  const gender = searchParams.get("gender");

  const getPlanDetails = () => {
    if (gender === "female") {
      switch(category) {
        case "underweight":
          return {
            title: "Weight Gain Plan (Female)",
            description: "For underweight females (BMI < 18.5)",
            goal: "Gain lean mass & support hormonal health",
            color: "#f39c12",
            workouts: [
              "🏋️‍♀️ Gym Workout (4–5 days/week)",
              "Focus: Compound lifts, glute/hip emphasis",
              "Strength Training (Heavy Weights, 4 sets x 6–10 reps)",
              "• Hip Thrusts or lie on your back and lift hips up; add weight with a backpack if needed",
              "• Bulgarian Split Squats or split squats - do lunges in place or use a low step for elevation",
              "• Deadlifts or backpack deadlifts - hinge at hips holding a backpack or water jugs",
              "• Overhead Press or bottle press - press filled water bottles or cans overhead",
              "• Pull-downs or Assisted Pull-ups or towel rows - Use a towel on a door handle",
              "• Cable Kickbacks or donkey kicks: Kick leg upward from all fours",
              "Cardio (1–2 days/week, Low Intensity): Incline Walk or Light Spin Bike (20 min max)",
              "",
              "🏠 Home Workout Options:",
              "• Glute Bridges (with pause) – 3x20",
              "• Step-Ups (use stairs or a chair) – 3x12",
              "• Banded Side Walks – 3x15",
              "• Push-ups (knees or incline if needed) – 3x10",
              "• Resistance Band Rows – 3x15"
            ],
            diet: [
              "High-Calorie, Nutrient-Dense with Hormone Support",
              "Protein: Eggs, Salmon, Greek Yogurt, Tofu, Cottage Cheese",
              "Carbs: Sweet Potato, Whole Wheat Pasta, Oats, Fruits",
              "Fats: Flaxseeds, Nut Butters, Olive Oil, Full-fat Dairy"
            ],
            tips: [
              "Include healthy fats daily to support estrogen production and menstrual regularity",
              "Focus on compound movements for balanced muscle growth",
              "Add calorie-dense snacks between meals like trail mix or smoothies"
            ]
          };
        case "normal":
          return {
            title: "Maintenance Plan (Female)",
            description: "For normal weight females (BMI 18.5–24.9)",
            goal: "Maintain strength, shape & hormonal balance",
            color: "#3ebd45",
            workouts: [
              "🏋️‍♀️ Gym Workout (3–4 days/week)",
              "Strength + Functional Movement Mix (8–12 reps)",
              "• Sumo Squats - feet wider than shoulders, toes slightly out",
              "• Cable Rows - sit on floor or lean back at an angle and pull towards chest",
              "• Incline Dumbbell Press - use chair/bed/stairs for incline push-ups",
              "• RDLs (Romanian Deadlifts) - keep slight knee bend, push hips back",
              "• Kettlebell Swings - use weighted backpack between legs to chest level",
              "Cardio (2 days/week): Dance class, Zumba, Jogging, or Moderate Cycling (20–30 min)",
              "",
              "🏠 Home Workout Options:",
              "• Plank to Push-up – 3x10",
              "• Glute Bridges + Banded Abductions – 3x20",
              "• Jumping Jacks – 3x50",
              "• Step-ups or Chair Squats – 3x12",
              "Yoga (once a week for flexibility & stress relief)"
            ],
            diet: [
              "Balanced Macronutrients with Micronutrient Support",
              "Protein: Eggs, Lentils, Fish, Chia Seeds",
              "Carbs: Quinoa, Brown Rice, Whole Wheat Bread",
              "Fats: Avocado, Seeds, Coconut Oil"
            ],
            tips: [
              "Rotate protein sources weekly to ensure B-vitamin diversity (great for skin & energy)",
              "Include weight training to maintain bone density",
              "Practice mindful eating to maintain healthy weight naturally"
            ]
          };
        case "overweight":
          return {
            title: "Fat Loss Plan (Female)",
            description: "For overweight females (BMI ≥ 25)",
            goal: "Lose fat, preserve lean mass & support metabolism",
            color: "#e74c3c",
            workouts: [
              "🏋️‍♀️ Gym Workout (4–5 days/week)",
              "HIIT (3x/week):",
              "• Treadmill intervals: sprint 30 sec, walk 1 min (repeat 8–10 times)",
              "• Kettlebell swings with weighted backpack",
              "• Rowing + Battle rope supersets or shadow slams",
              "Strength Training (2–3x/week, 12–15 reps):",
              "• Dumbbell Step-ups",
              "• Leg Press",
              "• Cable Pull-throughs",
              "• Arnold Press",
              "",
              "🏠 Home Workout Options:",
              "HIIT Circuit (repeat x3):",
              "• Jump squats – 20 sec",
              "• Mountain Climbers – 20 sec",
              "• Banded Squats – 15 reps",
              "• High Knees – 30 sec",
              "• Plank with Shoulder Taps – 30 sec"
            ],
            diet: [
              "Mild Calorie Deficit + High Protein + Anti-inflammatory Focus",
              "Protein: Lean meats, Legumes, Greek yogurt, Egg whites",
              "Carbs: Vegetables, Berries, Oats",
              "Fats: Chia Seeds, Avocado, Nuts (in moderation)"
            ],
            tips: [
              "Don't skip carbs entirely—fiber-rich carbs help with satiety & hormone balance",
              "Strength training preserves muscle during fat loss",
              "Stay hydrated and manage stress for better metabolic function"
            ]
          };
        default:
          return getDefaultPlan();
      }
    } else {
      switch(category) {
        case "underweight":
          return {
            title: "Weight Gain Plan",
            description: "For underweight individuals (BMI < 18.5)",
            goal: "Gain Healthy Weight & Muscle",
            color: "#f39c12",
            workouts: [
              "Strength Training (Heavy Weights, Low Reps, 3-5 sets each)",
              "• Squats",
              "• Deadlifts",
              "• Bench Press",
              "• Shoulder Press",
              "Cardio (2 days/week, Light Intensity)"
            ],
            diet: [
              "High-Calorie, High-Protein Diet",
              "Protein Sources: Chicken, Fish, Eggs, Paneer",
              "Carbs: Brown Rice, Oats, Whole Wheat Bread",
              "Fats: Avocado, Nuts, Olive Oil"
            ],
            tips: [
              "Focus on progressive overload in your lifts",
              "Eat every 2-3 hours to meet calorie needs"
            ]
          };
        case "normal":
          return {
            title: "Maintenance Plan",
            description: "For normal weight individuals (BMI 18.5-24.9)",
            goal: "Maintain Current Weight & Stay Fit",
            color: "#3ebd45",
            workouts: [
              "Strength Training (Moderate Weights, 8-12 reps)",
              "• Squats",
              "• Bench Press",
              "• Pull-ups",
              "• Shoulder Press",
              "Cardio (2-3 days/week, Moderate Intensity)"
            ],
            diet: [
              "Balanced Macronutrient Intake",
              "Protein Sources: Chicken, Tofu, Beans",
              "Carbs: Brown Rice, Quinoa, Whole Grains",
              "Healthy Fats: Almonds, Olive Oil"
            ],
            tips: [
              "Vary your workout routine to prevent plateaus",
              "Focus on compound movements for efficiency"
            ]
          };
        case "overweight":
          return {
            title: "Fat Loss Plan",
            description: "For overweight individuals (BMI ≥ 25)",
            goal: "Lose Fat & Improve Muscle Definition",
            color: "#e74c3c",
            workouts: [
              "HIIT (High-Intensity Workouts, 3-4 days/week)",
              "• Treadmill Sprints",
              "• Rowing Machine",
              "• Battle Ropes",
              "Strength Training (12-15 reps)"
            ],
            diet: [
              "Calorie Deficit with High Protein & Fiber",
              "Lean Protein: Chicken breast, Egg whites",
              "Low-Calorie Carbs: Oats, Brown Rice, Vegetables",
              "Healthy Fats: Nuts (small portions)"
            ],
            tips: [
              "Prioritize protein intake to preserve muscle",
              "Incorporate both cardio and strength training"
            ]
          };
        default:
          return getDefaultPlan();
      }
    }
  };

  const getDefaultPlan = () => ({
    title: "Custom Plan",
    description: "General health improvement plan",
    goal: "Improve Overall Health",
    color: "#3498db",
    workouts: [
      "Mix of Strength and Cardio",
      "3-4 days/week of moderate exercise"
    ],
    diet: [
      "Balanced diet with variety",
      "Focus on whole foods",
      "Stay hydrated"
    ],
    tips: [
      "Listen to your body and adjust as needed",
      "Consistency is more important than perfection"
    ]
  });

  const plan = getPlanDetails();

  const getSampleMealPlan = () => {
    if (gender === "female") {
      switch(category) {
        case "underweight":
          return (
            <>
              <p><strong>Breakfast:</strong> Oats + Almond butter + Berries + Protein shake</p>
              <p><strong>Lunch:</strong> Grilled tofu or paneer + Rice + Steamed veggies + Yogurt</p>
              <p><strong>Snack:</strong> Trail mix (nuts + seeds + dried fruit) + Milk</p>
              <p><strong>Dinner:</strong> Chicken thighs + Sweet potato + Avocado salad</p>
              <p><strong>Before Bed:</strong> Full-fat milk + Dates or dark chocolate</p>
            </>
          );
        case "normal":
          return (
            <>
              <p><strong>Breakfast:</strong> Greek yogurt + Muesli + Banana</p>
              <p><strong>Lunch:</strong> Chickpea salad + Brown rice + Stir-fried greens</p>
              <p><strong>Snack:</strong> Apple + Almonds</p>
              <p><strong>Dinner:</strong> Baked salmon + Veggie soup + Whole grain toast</p>
              <p><strong>Before Bed:</strong> Herbal tea + Small cube of dark chocolate</p>
            </>
          );
        case "overweight":
          return (
            <>
              <p><strong>Breakfast:</strong> Protein smoothie (spinach + banana + protein + flax)</p>
              <p><strong>Lunch:</strong> Grilled chicken breast + Cauliflower rice + Roasted veggies</p>
              <p><strong>Snack:</strong> Boiled eggs + Cucumber sticks</p>
              <p><strong>Dinner:</strong> Lentil stew + Mixed greens + Quinoa</p>
              <p><strong>Before Bed:</strong> Chamomile tea</p>
            </>
          );
        default:
          return (
            <>
              <p><strong>Breakfast:</strong> Balanced meal with protein, carbs and fats</p>
              <p><strong>Lunch:</strong> Varied whole foods</p>
              <p><strong>Dinner:</strong> Nutrient-dense options</p>
            </>
          );
      }
    } else {
      switch(category) {
        case "underweight":
          return (
            <>
              <p><strong>Breakfast:</strong> Scrambled eggs + Whole wheat toast + Peanut butter + Banana smoothie</p>
              <p><strong>Lunch:</strong> Grilled chicken + Brown rice + Vegetables + Yogurt</p>
              <p><strong>Dinner:</strong> Salmon + Quinoa + Avocado salad</p>
            </>
          );
        case "normal":
          return (
            <>
              <p><strong>Breakfast:</strong> Oats with nuts & honey + Boiled eggs</p>
              <p><strong>Lunch:</strong> Grilled fish + Quinoa + Roasted vegetables</p>
              <p><strong>Dinner:</strong> Stir-fried tofu with brown rice & salad</p>
            </>
          );
        case "overweight":
          return (
            <>
              <p><strong>Breakfast:</strong> Boiled eggs + Spinach smoothie + Whole grain toast</p>
              <p><strong>Lunch:</strong> Grilled chicken + Steamed broccoli + Brown rice</p>
              <p><strong>Dinner:</strong> Lentil soup + Mixed salad + Cottage cheese</p>
            </>
          );
        default:
          return (
            <>
              <p><strong>Breakfast:</strong> Balanced meal with protein, carbs and fats</p>
              <p><strong>Lunch:</strong> Varied whole foods</p>
              <p><strong>Dinner:</strong> Nutrient-dense options</p>
            </>
          );
      }
    }
  };

  return (
    <div className="diet-plan-container">
      <div className="plan-header">
        <h1>{plan.title}</h1>
        <div className="header-divider"></div>
        <h3>{plan.description}</h3>
        <div className="goal-pill" style={{ backgroundColor: plan.color }}>
          {plan.goal}
        </div>
      </div>
      
      <div className="plan-content">
        <div className="plan-section">
          <h2>
            <span className="section-icon">💪</span>
            Workout Plan
          </h2>
          <ul className="workout-list">
            {plan.workouts.map((item, index) => (
              <li key={index} className={item.startsWith("🏠") ? "home-workout-header" : ""}>
                {item.startsWith("•") ? (
                  <>
                    <span className="list-bullet" style={{ color: plan.color }}>•</span>
                    {item.substring(1)}
                  </>
                ) : (
                  item === "" ? <br key={index} /> : item
                )}
              </li>
            ))}
          </ul>
        </div>
        
        <div className="plan-section">
          <h2>
            <span className="section-icon">🥗</span>
            Diet Plan
          </h2>
          <ul>
            {plan.diet.map((item, index) => (
              <li key={index}>
                <span className="list-bullet" style={{ color: plan.color }}>•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {plan.tips && plan.tips.length > 0 && (
          <div className="plan-section tips-section">
            <h2>
              <span className="section-icon">💡</span>
              {gender === "female" ? "Female-Specific Tips" : "Important Tips"}
            </h2>
            <ul>
              {plan.tips.map((tip, index) => (
                <li key={index}>
                  <span className="list-bullet" style={{ color: plan.color }}>•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <div className="sample-meal" style={{ borderColor: plan.color }}>
          <h3>
            <span className="meal-icon">🍽️</span>
            Sample Meal Plan
          </h3>
          <div className="meal-plan">
            {getSampleMealPlan()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietPlan;